/**
 * ══════════════════════════════════════════════════════════════════════════
 * PUAN ÖZET SATIRI — TEK SUNUM BİLEŞENİ
 * ══════════════════════════════════════════════════════════════════════════
 *
 * ── NİYE VAR (owner, 2026-09-14) ─────────────────────────────────────────
 * *"Puan özeti ortak bileşen değil: `.pm-meta` ve `.ppv-meta` ayrı JSX olarak
 * duruyor. Aynı konumda bulunmaları ortaklaştırmanın tamamlandığı anlamına
 * gelmiyor."*  — Haklı bir itiraz: önceki tur yalnız KONUMU ortaklaştırdı
 * (üç yüzeyde de satır aksiyonların altına indi), KARARI değil.
 *
 * ÖLÇÜLEN AYRIŞMA (kaynaktan, 2026-09-14): `.pm-meta` ile `.postDk-meta`
 * yapı olarak BİREBİR aynıydı (ortalama span + "Puanın" hapı) ve iki ayrı
 * JSX bloğunda yaşıyordu; `.ppv-meta` ise klip-otoritesi dallarını taşıyan
 * ÜÇÜNCÜ bir blok. Yani "aynı bilgi, üç kopya".
 *
 * ── BURADA YAŞAYAN KARAR ─────────────────────────────────────────────────
 *   1. sözcük:  "Ortalama …"  ↔  "Puanın: …"   (kimin puanı olduğu metinde)
 *   2. biçim:   düz metin      ↔  kenarlıklı hap
 *   3. sıra:    ortalama önce, kendi puanın sonra
 *   4. satır SARAR, taşmaz; hiçbiri kendi tam-genişlik satırını AÇMAZ
 *   5. klip yüzeylerinde eşik altında SAYI yerine kanonun dili (`klipYildiz`)
 *
 * ── SINIF ÖNEKİ ──────────────────────────────────────────────────────────
 * Yüzeyler kendi `pm-*` / `ppv-*` / `postDk-*` adlarını sürdürür (renk,
 * dolgu ve bu adlara bağlı mevcut kapılar). Ortaklaşan şey KARAR.
 * ⚠️ Sınıf adları LİTERAL tabloda: `dokunma:kapi`/`tembel:kapi` gibi statik
 * çözümleyiciler çalışma anında kurulan adları GÖREMEZ (2026-09-14'te
 * PostActionsBar'da ölçüldü; iki kapı birden yanlış hüküm verdi).
 */
import React from "react";

export interface KlipYildizOzeti {
  mod: string;
  metin: string;
  count: number;
  erisilebilirlik?: string;
}

export interface PostRatingSummaryProps {
  onek: "pm" | "ppv" | "postDk";
  /** `content/{id}.agg` — ortalama + oy sayısı. Yoksa satır bu bilgiyi taşımaz. */
  ortalama?: { avg: number; count: number } | null;
  /** Klip otoritesi (yalnız klip taşıyan yüzeylerde). Varsa ortalamanın YERİNE geçer. */
  klipYildiz?: KlipYildizOzeti | null;
  /** Klip otoritesi yokken ham oy sayısı (ppv mirası: "N puan"). */
  oySayisi?: number;
  /** Cihaz-kalıcı "Puanın: X★" (localStorage; Firestore okuması YOK). */
  benimPuanim?: number | null;
  /** Kendi içeriği — satır, puan bilgisi olmasa da doğabilir (yüzey kararı). */
  kendiIcerigi?: boolean;
  sayiBicimle: (n: number) => React.ReactNode;
}

const SINIFLAR = {
  pm: { kap: "pab-meta pm-meta", ort: "pm-starmeta pm-meta-avg", benim: "pm-meta-mine", sayi: "pm-num" },
  ppv: { kap: "pab-meta ppv-meta", ort: "ppv-starmeta ppv-meta-avg", benim: "ppv-meta-mine", sayi: "ppv-num" },
  postDk: { kap: "pab-meta postDk-meta", ort: "postDk-starMeta postDk-meta-avg", benim: "postDk-meta-mine", sayi: "postDk-num" },
} as const;

export default function PostRatingSummary({
  onek,
  ortalama = null,
  klipYildiz = null,
  oySayisi = 0,
  benimPuanim = null,
  kendiIcerigi = false,
  sayiBicimle,
}: PostRatingSummaryProps): React.ReactElement | null {
  const c = SINIFLAR[onek];
  const ortVar = !!(ortalama && ortalama.avg > 0 && ortalama.count > 0);
  const benimVar = benimPuanim != null && benimPuanim > 0;
  const klipVar = !!klipYildiz;
  const oyVar = oySayisi > 0;
  if (!ortVar && !benimVar && !klipVar && !oyVar && !kendiIcerigi) return null;

  return (
    <div className={c.kap}>
      {klipVar ? (
        /* KLİP — TEK OTORİTE. Eşik altında sayı yerine kanonun dili;
           hiç oy yoksa "N puan" satırı korunur. */
        klipYildiz.mod === "sayi" || klipYildiz.mod === "yolculuk" ? (
          <span className={c.ort} aria-label={klipYildiz.erisilebilirlik}>
            {klipYildiz.mod === "sayi" ? `${klipYildiz.metin}★` : klipYildiz.metin} ·{" "}
            {sayiBicimle(klipYildiz.count)} puan
          </span>
        ) : oyVar ? (
          <span className={c.sayi}>{sayiBicimle(oySayisi)} puan</span>
        ) : null
      ) : ortVar ? (
        <span className={c.ort} aria-label="Bu gönderinin ortalama puanı">
          Ortalama {Number(ortalama!.avg).toFixed(1)}★ · {sayiBicimle(ortalama!.count)} puan
        </span>
      ) : oyVar ? (
        <span className={c.sayi}>{sayiBicimle(oySayisi)} puan</span>
      ) : null}

      {/* SENİN PUANIN — ortalamadan üç bağımsız sinyalle ayrılır (sözcük ·
          biçim · ağırlık). Sözcük popover'daki «Puanın: 4★ · değiştirebilirsin»
          ile BİREBİR aynı: aynı bilgi iki yüzeyde iki dille konuşmaz. */}
      {benimVar && (
        <span
          className={c.benim}
          title="Senin verdiğin puan"
          aria-label={`Senin puanın: ${benimPuanim} yıldız`}
        >
          Puanın: {benimPuanim}★
        </span>
      )}
    </div>
  );
}
